import React, { useMemo } from 'react';
import { Box, capitalizeFirstLetter, Inscription, ShimmerBox } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import InfoTileIcon from '@/components/elements/InfoTile/InfoTileIcon';
import useGetActivityLogsQuery, { Actions } from '@/modules/ActivityLog/LogList/useGetActivityLogs.query';
import { LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

import useGetClaimRepaymentCompleteStatus from './hooks/useGetClaimRepaymentCompleteStatus';
import useGetReturnCheckStatus from './hooks/useGetReturnCheckStatus';
import { StatusButton } from './LockboxStatus.styles';

const LockboxStatusHeader = () => {
  const { originalLockbox } = useLockboxDetailsStore();
  const { getClaimRepaymentCompleteStatus } = useGetClaimRepaymentCompleteStatus();
  const { getReturnCheckStatus } = useGetReturnCheckStatus();

  const { logs, isLoading } = useGetActivityLogsQuery({
    lockboxId: originalLockbox?.id,
    page: 1,
    size: 100,
    actions: [Actions.update, Actions.create],
    disabled: !originalLockbox?.id,
  });

  const newestLog = useMemo(() => {
    const sortedLogs = [...(logs || [])]
      .sort((prev, next) => dayjs(next.changedAt)
      .valueOf() - dayjs(prev.changedAt).valueOf());
    return sortedLogs?.[0];
  }, [logs]);

  const fullName = useMemo(() => `${newestLog?.changedByName}`.trim(), [newestLog?.changedByName]);

  const updatedAt = useMemo(
    () => `${dayjs(newestLog?.changedAt).format('D MMM')}
    at ${dayjs(newestLog?.changedAt).format('h:mm a')}`,
    [newestLog?.changedAt],
  );

  const lockboxStatus = useMemo(() => {
    if (originalLockbox?.lockboxType === LockboxType.CLAIM_REPAYMENT) {
      return getClaimRepaymentCompleteStatus();
    }

    if (originalLockbox?.lockboxType === LockboxType.RETURNED_CHECK) {
      return getReturnCheckStatus();
    }

    return { status: originalLockbox?.lockboxStatus, statusTitle: originalLockbox?.lockboxStatus };
  }, [originalLockbox?.lockboxStatus, originalLockbox?.lockboxType, getClaimRepaymentCompleteStatus, getReturnCheckStatus]);

  return (
    <Box direction="row" data-testid="lockbox-status-card_header">
      {isLoading ? (
        <ShimmerBox
          height='40px'
          width='40px'
          margin={{ right: 's' }}
          style={{ position: 'relative' }}
        />
      ) : (
        <Box
          data-testid="lockbox-status-card_changed-by_icon"
          width="40px"
          height="40px"
          margin={{ right: 's' }}
          style={{ position: 'relative' }}
        >
          <InfoTileIcon title={fullName} />
        </Box>
      )}

      <Box direction="column">
        <Box direction="row" align="center">
          {isLoading ? <ShimmerBox height='20px' width='200px' margin={{ right: 'xxsmall' }} /> : (
            <Inscription
              data-testid="lockbox-status-card_changed-by"
              weight="bold"
              margin={{ right: 'xxsmall' }}
              lineHeight="20px"
            >
              {fullName}
            </Inscription>
          )}
        </Box>
        {isLoading ? <ShimmerBox height='20px' width='120px' margin={{ right: 'small', top: 'spacing2' }} /> : (
          <Inscription
            data-testid="lockbox-status-card_updated-at"
            color="textSecondary"
            margin={{ right: 'small', top: 'spacing2' }}
            lineHeight="20px"
          >
            {updatedAt}
          </Inscription>
        )}
      </Box>

      <Box margin={{ left: 'auto'}}>
        <StatusButton
          data-testid="lockbox-status-card_status"
          size="medium"
          type="button"
          label={capitalizeFirstLetter(lockboxStatus?.statusTitle || '').replace('_', ' ')}
          status={lockboxStatus?.status}
        />
      </Box>
    </Box>
  );
};

export default LockboxStatusHeader;
